import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Divider } from "primereact/divider";
import { Panel } from "primereact/panel";
import { Tag } from "primereact/tag";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import MeaMemberAvatar from "../../../../components/MeaMemberAvatar";
import MeaLink from "../../../../components/MeaLink";
import MeaStudentAvatar from "../../../trainings/components/MeaStudentAvatar";
import MeaStudentStatus2 from "../../../../components/MeaStudentStatus2";
import { PERMISSION_TRAINING_CLASSES, PERMISSION_TRAINING_LEVELS, PERMISSION_TRAINING_STUDENTS } from "../../../../permissions";
import studentsAPI from "../../../../services/students";
import scheduleAPI from "../../../../services/schedule";
import ScheduleListComponent from "../../schedule/components/ScheduleListComponent";
import StudentAttendancesPanel from "./StudentAttendancesPanel";
import StudentSlotChangeRequestsPanel from "./StudentSlotChangeRequestsPanel";
import StudentObservationsPanel from "./StudentObservationsPanel";
import { formatSlotLabel } from "../../slot-change-requests/slotChangeUtils";
import MemberRegistrationsTimelineComponent from "../../../school/members/components/MemberRegistrationsTimelineComponent";

const StudentInformationContent = ({
  student,
  schoolId,
  trainingId,
  siblingLink = "students",
  showHistory = false,
  showStudentFileButton = true,
  topPanelHeader = "Inscription",
  disableNavigation = false,
}) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" });
  const [copiedKey, setCopiedKey] = useState(null);
  const siblingsQuery = studentsAPI.ListSiblings(schoolId, trainingId, "", student?.id);
  const siblings = siblingsQuery?.data?.data;
  const scheduleQuery = scheduleAPI.useGetList(schoolId, trainingId, "", 1, 1000, {});

  const copyToClipboard = async (key, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey((currentKey) => currentKey === key ? null : currentKey), 1400);
    } catch (error) {
      console.error("Failed to copy value to clipboard", error);
    }
  };

  const siblingScheduleItemsForClass = (classId) =>
    (scheduleQuery?.data?.data || [])
      .filter((item) => (item?.class_id || item?.classe?.id) === classId)
      .filter((item, index, items) => {
        const slotId = item?.slot_id || item?.slot?.id || "";
        return slotId === "" || items.findIndex((candidate) => (candidate?.slot_id || candidate?.slot?.id || "") === slotId) === index;
      });

  const teacherDisplayName = (teacher) => {
    if (!teacher?.member) {
      return "-";
    }
    if (teacher.member.name) {
      return teacher.member.name;
    }
    if (teacher.member.lastname && teacher.member.firstname) {
      return `${teacher.member.lastname} ${teacher.member.firstname}`;
    }
    return teacher.member.lastname || teacher.member.firstname || "-";
  };

  const siblingSlotDisplayLabel = (slot) => formatSlotLabel(slot).replace(/\s*\([^)]*\)\s*$/, "");

  const renderSiblingStudent = (item) => (
    <MeaStudentAvatar
      permission={PERMISSION_TRAINING_STUDENTS.GET}
      student={item}
      schoolId={schoolId}
      trainingId={trainingId}
      link={siblingLink}
      noNavigation={disableNavigation}
    />
  );

  const renderSiblingLevel = (item) => (
    <MeaLink
      to={`/school/${schoolId}/admin/trainings/${trainingId}/levels/${item.level_id}`}
      permission={PERMISSION_TRAINING_LEVELS.GET}
      noNavigation={disableNavigation}
    >
      {item.level?.name}
    </MeaLink>
  );

  const renderSiblingClass = (item) => {
    const scheduleItems = siblingScheduleItemsForClass(item?.class_id || item?.classe?.id);
    return (
      <div className="inline-flex flex-column gap-1">
        <MeaLink
          to={`/school/${schoolId}/admin/trainings/${trainingId}/classes/${item.class_id}`}
          permission={PERMISSION_TRAINING_CLASSES.GET}
          noNavigation={disableNavigation}
        >
          {item.classe?.name}
        </MeaLink>
        {scheduleItems.length > 0 ? (
          <small className="text-500 flex flex-column gap-1">
            {scheduleItems.map((scheduleItem) => (
              <span key={scheduleItem.id}>
                {siblingSlotDisplayLabel(scheduleItem?.slot)}
                {" | "}
                {teacherDisplayName(scheduleItem?.teacher)}
                {" | "}
                {scheduleItem?.classroom?.name || "-"}
              </span>
            ))}
          </small>
        ) : null}
      </div>
    );
  };

  if (!student) {
    return null;
  }

  return (
    <div className="mea-student-sheet">
      <div className="mb-4">
        <Panel header={topPanelHeader} toggleable>
          <div className="grid">
            <div className="col-12 md:col-6">
              <ul className="list-none p-0 m-0">
                <li key="firstname" className="flex align-items-start py-2 px-2 flex-wrap">
                  <div className="text-500 w-full md:w-5 font-medium">Prénom</div>
                  <div className="text-900 w-full pl-3 md:w-7">
                    <div className="flex justify-content-between flex-wrap align-items-center">
                      <div className="w-full">{student?.firstname || "-"}</div>
                    </div>
                  </div>
                </li>
                <li key="lastname" className="flex align-items-start py-2 px-2 flex-wrap">
                  <div className="text-500 w-full md:w-5 font-medium">Nom</div>
                  <div className="text-900 w-full pl-3 md:w-7">
                    <div className="flex justify-content-between flex-wrap align-items-center">
                      <div className="w-full">{student?.lastname || "-"}</div>
                    </div>
                  </div>
                </li>
                <li key="member_id" className="flex align-items-start py-2 px-2 flex-wrap">
                  <div className="text-500 w-full md:w-5 font-medium">Immatriculation</div>
                  <div className="text-900 w-full pl-3 md:w-7">
                    <div className="flex justify-content-between flex-wrap align-items-center">
                      <div className="w-full">{student?.member_id || "-"}</div>
                    </div>
                  </div>
                </li>
                <li key="birthday" className="flex align-items-start py-2 px-2 flex-wrap">
                  <div className="text-500 w-full md:w-5 font-medium">Date de naissance</div>
                  <div className="text-900 w-full pl-3 md:w-7">
                    <div className="flex justify-content-between flex-wrap align-items-center">
                      <div className="w-full">{student?.birthday || "-"}</div>
                    </div>
                  </div>
                </li>
                <li key="postal_code" className="flex align-items-start py-2 px-2 flex-wrap">
                  <div className="text-500 w-full md:w-5 font-medium">Code postal</div>
                  <div className="text-900 w-full pl-3 md:w-7">
                    <div className="flex justify-content-between flex-wrap align-items-center">
                      <div className="w-full">{student?.postal_code || "-"}</div>
                    </div>
                  </div>
                </li>
                {showStudentFileButton ? (
                  <li key="candidate-file" className="flex align-items-start py-2 px-2 flex-wrap">
                    <div className="text-500 w-full md:w-5 font-medium">Dossier élève</div>
                    <div className="text-900 w-full pl-3 md:w-7 h-full">
                      <div className="flex justify-content-between flex-wrap align-items-center h-full">
                        <div className="w-full h-full">
                          {student?.id && !disableNavigation ? (
                            <Button
                              label="Voir le dossier complet"
                              icon="pi pi-id-card"
                              size="small"
                              severity="secondary"
                              outlined
                              rounded
                              className="h-full"
                              onClick={() => navigate(`/school/${schoolId}/admin/registrations/${trainingId}/candidates/${student.id}`)}
                            />
                          ) : student?.id ? (
                            <span className="text-700">Dossier élève disponible</span>
                          ) : (
                            "-"
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ) : null}
              </ul>
            </div>
            <div className="col-12 md:col-6">
              <ul className="list-none p-0 m-0">
                <li key="status" className="flex align-items-start py-2 px-2 flex-wrap">
                  <div className="text-500 w-full md:w-5 font-medium">Status</div>
                  <div className="text-900 w-full pl-3 md:w-7">
                    <div className="flex justify-content-between flex-wrap align-items-center">
                      <div className="w-full"><MeaStudentStatus2 student={student} /></div>
                    </div>
                  </div>
                </li>
                <li key="level" className="flex align-items-start py-2 px-2 flex-wrap">
                  <div className="text-500 w-full md:w-5 font-medium">Niveau</div>
                  <div className="text-900 w-full pl-3 md:w-7">
                    <div className="flex justify-content-between flex-wrap align-items-center">
                      <div className="w-full">
                        {student?.level?.name ? (
                          <MeaLink
                            to={`/school/${schoolId}/admin/trainings/${trainingId}/levels/${student.level.id}`}
                            permission={PERMISSION_TRAINING_LEVELS.GET}
                            noNavigation={disableNavigation}
                          >
                            {student.level.name}
                          </MeaLink>
                        ) : (
                          "-"
                        )}
                      </div>
                    </div>
                  </div>
                </li>
                <li key="classe" className="flex align-items-start py-2 px-2 flex-wrap">
                  <div className="text-500 w-full md:w-5 font-medium">Classe</div>
                  <div className="text-900 w-full pl-3 md:w-7">
                    <div className="flex justify-content-between flex-wrap align-items-center">
                      <div className="w-full">
                        {student?.classe?.name ? (
                          <MeaLink
                            to={`/school/${schoolId}/admin/trainings/${trainingId}/classes/${student.classe.id}`}
                            permission={PERMISSION_TRAINING_CLASSES.GET}
                            noNavigation={disableNavigation}
                          >
                            {student.classe.name}
                          </MeaLink>
                        ) : (
                          "-"
                        )}
                      </div>
                    </div>
                  </div>
                </li>
                <li key="contract" className="flex align-items-start py-2 px-2 flex-wrap">
                  <div className="text-500 w-full md:w-5 font-medium">Contrat</div>
                  <div className="text-900 w-full pl-3 md:w-7">
                    <div className="flex justify-content-between flex-wrap align-items-center">
                      <div className="w-full">
                        {student?.contract_id && !disableNavigation ? (
                          <div className="flex flex-column gap-2">
                            <span>{student.contract_id}</span>
                            <Button
                              label="Voir le contrat"
                              icon="pi pi-file"
                              size="small"
                              severity="secondary"
                              outlined
                              rounded
                              onClick={() => navigate(`/school/${schoolId}/admin/registrations/${trainingId}/contracts/${student.contract_id}`)}
                            />
                          </div>
                        ) : student?.contract_id ? (
                          <span>{student.contract_id}</span>
                        ) : (
                          "-"
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </Panel>
      </div>

      <div className="grid">
        <div className="col-12 md:col-6">
          <Panel header="Responsables légaux" toggleable>
            <div className="grid">
              <div className="col-12">
                <div className="p-3 border-1 surface-border border-round surface-50">
                  <div className="font-medium text-900 mb-2">Père</div>
                  <div className="text-900 mb-2">{((student?.father_lastname || "") + " " + (student?.father_firstname || "")).trim() || "-"}</div>
                  <div className="flex flex-column gap-2">
                    {student?.father_phone ? (
                      <div className="flex flex-wrap gap-2 align-items-center">
                        <Button
                          size="small"
                          icon="pi pi-phone"
                          label={student?.father_phone}
                          severity="secondary"
                          outlined
                          onClick={() => {
                            window.open("tel:" + student?.father_phone);
                          }}
                        />
                        <Button
                          size="small"
                          icon={copiedKey === "father_phone" ? "pi pi-check" : "pi pi-copy"}
                          label={copiedKey === "father_phone" ? "Copié" : "Copier"}
                          text
                          severity="secondary"
                          onClick={() => copyToClipboard("father_phone", student?.father_phone)}
                        />
                      </div>
                    ) : (
                      <span className="text-500">-</span>
                    )}
                    {student?.father_email ? (
                      <div className="flex flex-wrap gap-2 align-items-center">
                        <Button
                          size="small"
                          icon="pi pi-envelope"
                          label={student?.father_email}
                          text
                          onClick={() => {
                            window.open("mailto:" + student?.father_email);
                          }}
                        />
                        <Button
                          size="small"
                          icon={copiedKey === "father_email" ? "pi pi-check" : "pi pi-copy"}
                          label={copiedKey === "father_email" ? "Copié" : "Copier"}
                          text
                          severity="secondary"
                          onClick={() => copyToClipboard("father_email", student?.father_email)}
                        />
                      </div>
                    ) : (
                      <span className="text-500">-</span>
                    )}
                  </div>
                  <Divider className="my-2" />
                  <div>
                    {student?.father ? (
                      <MeaMemberAvatar member={student?.father} noNavigation={disableNavigation} />
                    ) : (
                      "Aucun membre lié"
                    )}
                  </div>
                </div>
              </div>

              <div className="col-12">
                <div className="p-3 border-1 surface-border border-round surface-50">
                  <div className="font-medium text-900 mb-2">Mère</div>
                  <div className="text-900 mb-2">{((student?.mother_lastname || "") + " " + (student?.mother_firstname || "")).trim() || "-"}</div>
                  <div className="flex flex-column gap-2">
                    {student?.mother_phone ? (
                      <div className="flex flex-wrap gap-2 align-items-center">
                        <Button
                          size="small"
                          icon="pi pi-phone"
                          label={student?.mother_phone}
                          severity="secondary"
                          outlined
                          onClick={() => {
                            window.open("tel:" + student?.mother_phone);
                          }}
                        />
                        <Button
                          size="small"
                          icon={copiedKey === "mother_phone" ? "pi pi-check" : "pi pi-copy"}
                          label={copiedKey === "mother_phone" ? "Copié" : "Copier"}
                          text
                          severity="secondary"
                          onClick={() => copyToClipboard("mother_phone", student?.mother_phone)}
                        />
                      </div>
                    ) : (
                      <span className="text-500">-</span>
                    )}
                    {student?.mother_email ? (
                      <div className="flex flex-wrap gap-2 align-items-center">
                        <Button
                          size="small"
                          icon="pi pi-envelope"
                          label={student?.mother_email}
                          text
                          onClick={() => {
                            window.open("mailto:" + student?.mother_email);
                          }}
                        />
                        <Button
                          size="small"
                          icon={copiedKey === "mother_email" ? "pi pi-check" : "pi pi-copy"}
                          label={copiedKey === "mother_email" ? "Copié" : "Copier"}
                          text
                          severity="secondary"
                          onClick={() => copyToClipboard("mother_email", student?.mother_email)}
                        />
                      </div>
                    ) : (
                      <span className="text-500">-</span>
                    )}
                  </div>
                  <Divider className="my-2" />
                  <div>
                    {student?.mother ? (
                      <MeaMemberAvatar member={student?.mother} noNavigation={disableNavigation} />
                    ) : (
                      "Aucun membre lié"
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-2">
              <Tag
                value={student?.parents_divorced ? "Parents divorcés" : "Parents mariés"}
                severity={student?.parents_divorced ? "danger" : "success"}
                icon={student?.parents_divorced ? "pi pi-times-circle" : "pi pi-check-circle"}
              />
            </div>
          </Panel>

          <Panel header="Fratrie" toggleable className="mt-3">
            {siblings && siblings.length > 0 ? (
              isMobile ? (
                <div className="flex flex-column gap-2" data-testid="siblings-cards">
                  {siblings.map((item) => (
                    <div
                      key={item.id}
                      className="border-1 surface-border border-round p-3 flex flex-column gap-2"
                      data-testid={`sibling-card-${item.id}`}
                    >
                      <div className="flex justify-content-between align-items-start gap-2">
                        {renderSiblingStudent(item)}
                        <MeaStudentStatus2 student={item} />
                      </div>
                      <div className="flex flex-column gap-1 text-sm">
                        <div>
                          <span className="text-500 font-medium">Niveau : </span>
                          {renderSiblingLevel(item)}
                        </div>
                        <div>
                          <span className="text-500 font-medium">Classe : </span>
                          {renderSiblingClass(item)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <DataTable value={siblings} className="p-datatable-sm">
                  <Column field="id" header="Elève" body={renderSiblingStudent} />
                  <Column field="level_id" header="Niveau" body={renderSiblingLevel} />
                  <Column field="class_id" header="Classe" body={renderSiblingClass} />
                  <Column field="status" header="Status" body={(item) => <MeaStudentStatus2 student={item} />} />
                </DataTable>
              )
            ) : (
              <div className="text-center text-500 py-4">
                Aucun frère ou sœur inscrit dans cette école
              </div>
            )}
          </Panel>

          <Panel header="Personnes à contacter" toggleable className="mt-3">
            <ul className="list-none p-0 m-0">
              <li key="contact-1" className="flex align-items-start py-2 px-2 flex-wrap">
                <div className="text-500 w-full md:w-5 font-medium">Personne 1</div>
                <div className="text-900 w-full pl-3 md:w-7">
                  <div className="flex justify-content-between flex-wrap align-items-center">
                    <div className="w-full">{student?.contact_person_1 || "-"}</div>
                    <div className="w-full">
                      {student?.contact_person_1_phone ? (
                        <Button
                          size="small"
                          icon="pi pi-phone"
                          label={student?.contact_person_1_phone || "-"}
                          severity="secondary"
                          rounded
                          onClick={() => {
                            window.open("tel:" + student?.contact_person_1_phone);
                          }}
                        />
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>
                </div>
              </li>
              <li key="contact-2" className="flex align-items-start py-2 px-2 flex-wrap">
                <div className="text-500 w-full md:w-5 font-medium">Personne 2</div>
                <div className="text-900 w-full pl-3 md:w-7">
                  <div className="flex justify-content-between flex-wrap align-items-center">
                    <div className="w-full">{student?.contact_person_2 || "-"}</div>
                    <div className="w-full">
                      {student?.contact_person_2_phone ? (
                        <Button
                          size="small"
                          icon="pi pi-phone"
                          label={student?.contact_person_2_phone || "-"}
                          severity="secondary"
                          rounded
                          onClick={() => {
                            window.open("tel:" + student?.contact_person_2_phone);
                          }}
                        />
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </Panel>
        </div>

        <div className="col-12 md:col-6">
          <Panel header="Autorisations" toggleable>
            <ul className="list-none p-0 m-0">
              <li key="authorized_back_with" className="flex align-items-start py-2 px-2 flex-wrap">
                <div className="text-500 w-full md:w-5 font-medium">Récupérer l'enfant après les cours</div>
                <div className="text-900 w-full pl-3 md:w-7">
                  <div className="flex justify-content-between flex-wrap align-items-center">
                    <div className="w-full">{student?.authorized_back_with || "-"}</div>
                  </div>
                </div>
              </li>
              <li key="authorized_back_alone" className="flex align-items-start py-2 px-2 flex-wrap">
                <div className="text-500 w-full md:w-5 font-medium">Peut partir seul</div>
                <div className="text-900 w-full pl-3 md:w-7">
                  <div className="flex justify-content-between flex-wrap align-items-center">
                    <div className="w-full">
                      {student?.authorized_back_alone === true ? (
                        <i className="pi pi-check" style={{ color: "green" }}></i>
                      ) : student?.authorized_back_alone === false ? (
                        <i className="pi pi-times" style={{ color: "red" }}></i>
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>
                </div>
              </li>
              <li key="authorized_take_photo" className="flex align-items-start py-2 px-2 flex-wrap">
                <div className="text-500 w-full md:w-5 font-medium">Autorisation photo</div>
                <div className="text-900 w-full pl-3 md:w-7">
                  <div className="flex justify-content-between flex-wrap align-items-center">
                    <div className="w-full">
                      {student?.authorized_take_photo === true ? (
                        <i className="pi pi-check" style={{ color: "green" }}></i>
                      ) : student?.authorized_take_photo === false ? (
                        <i className="pi pi-times" style={{ color: "red" }}></i>
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </Panel>

          <Panel header="Renseignements médicaux" toggleable className="mt-3">
            <ul className="list-none p-0 m-0">
              <li key="health_issues" className="flex align-items-start py-2 px-2 flex-wrap">
                <div className="text-500 w-full md:w-5 font-medium">Problèmes de santé</div>
                <div className="text-900 w-full pl-3 md:w-7">
                  <div className="w-full">{student?.health_issues || "-"}</div>
                </div>
              </li>
              <li key="medical_traitement" className="flex align-items-start py-2 px-2 flex-wrap">
                <div className="text-500 w-full md:w-5 font-medium">Traitement médical</div>
                <div className="text-900 w-full pl-3 md:w-7">
                  <div className="w-full">{student?.medical_traitement || "-"}</div>
                </div>
              </li>
              <li key="allergies" className="flex align-items-start py-2 px-2 flex-wrap">
                <div className="text-500 w-full md:w-5 font-medium">Allergies</div>
                <div className="text-900 w-full pl-3 md:w-7">
                  <div className="w-full">{student?.allergies || "-"}</div>
                </div>
              </li>
              <li key="medical_devices" className="flex align-items-start py-2 px-2 flex-wrap">
                <div className="text-500 w-full md:w-5 font-medium">Equipements médicaux</div>
                <div className="text-900 w-full pl-3 md:w-7">
                  <div className="w-full">{student?.medical_devices || "-"}</div>
                </div>
              </li>
            </ul>
          </Panel>

          <Panel header="Programme de la semaine" toggleable className="mt-3">
            {student?.class_id ? (
              <ScheduleListComponent
                schoolId={schoolId}
                trainingId={trainingId}
                filter={{ classe_id: student?.class_id }}
                showAddButton={false}
                showFilterButton={false}
                showPaginator={false}
                pageSize={50}
                disableNavigation={disableNavigation}
                mobileCards
                selectedFields={{
                  selection: false,
                  classe: false,
                  edit: false,
                }}
              />
            ) : (
              <div className="text-center text-500 py-5">
                L'élève n'a pas encore été affecté à une classe
              </div>
            )}
          </Panel>

          <StudentObservationsPanel
            student={student}
            schoolId={schoolId}
            trainingId={trainingId}
            className="mt-3"
          />

          <StudentAttendancesPanel
            studentId={student?.id}
            schoolId={schoolId}
            trainingId={trainingId}
            className="mt-3"
          />

          <StudentSlotChangeRequestsPanel
            studentId={student?.id}
            schoolId={schoolId}
            trainingId={trainingId}
            className="mt-3"
          />
        </div>
      </div>

      {showHistory ? (
        <div className="mt-3">
          <Panel header="Historique des inscriptions" toggleable>
            <MemberRegistrationsTimelineComponent
              memberId={student?.member_id}
              schoolId={schoolId}
              fallbackStudentId={student?.id}
              disableNavigation={disableNavigation}
            />
          </Panel>
        </div>
      ) : null}
    </div>
  );
};

export default StudentInformationContent;
